import React from 'react';
import PropTypes from 'prop-types';

import {
    FB_LINK,
    INSTAGRAM_LINK,
    KOFI_LINK,
    SUPPORT_EMAIL,
    YOUTUBE_LINK,
} from '../../constants';
import {
    CourseCategory,
    CourseLanguage,
    CourseLevel,
    CourseMedium,
} from '../../models/enums';

const SITE_CONFIG = {
    fallbackSiteUrl: 'https://www.hok6.com',
    organizationName: '學識 Hok6',
    organizationAlternateName: 'Hok6',
    organizationLogoPath: '/img/logo192.webp',
    searchPathTemplate: '/search?query={search_term_string}',
    socialLinks: [
        FB_LINK,
        INSTAGRAM_LINK,
        YOUTUBE_LINK,
        KOFI_LINK,
    ],
};

export function getSiteUrl() {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
        return window.location.origin;
    }

    if (process.env.REACT_APP_FRONTEND_DOMAIN) {
        return `https://${process.env.REACT_APP_FRONTEND_DOMAIN}`;
    }

    return SITE_CONFIG.fallbackSiteUrl;
}

export function toAbsoluteUrl(url) {
    if (!url) {
        return null;
    }

    if (/^https?:\/\//.test(url)) {
        return url;
    }

    const siteUrl = getSiteUrl();
    return url.startsWith('/') ? `${siteUrl}${url}` : `${siteUrl}/${url}`;
}

export function stripHtml(value = '') {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function getDisplayNames(values = [], enumMap = {}) {
    return values
        .map(value => enumMap[value]?.displayName || value)
        .filter(Boolean);
}

function mergeStructuredData(baseData, extraData = {}) {
    return {
        ...baseData,
        ...Object.fromEntries(
            Object.entries(extraData).filter(([, value]) => value !== undefined),
        ),
    };
}

function buildImageObject(url) {
    const absoluteUrl = toAbsoluteUrl(url);

    if (!absoluteUrl) {
        return undefined;
    }

    return {
        '@type': 'ImageObject',
        url: absoluteUrl,
    };
}

function buildPersonReference(person) {
    if (!person?.displayName) {
        return undefined;
    }

    return {
        '@type': 'Person',
        name: person.displayName,
        url: person.id ? toAbsoluteUrl(`/user/${person.id}`) : undefined,
    };
}

function buildOrganizationReference(extraData = {}) {
    const siteUrl = getSiteUrl();

    return mergeStructuredData(
        {
            '@type': 'Organization',
            '@id': `${siteUrl}/#organization`,
            name: SITE_CONFIG.organizationName,
            url: siteUrl,
            logo: buildImageObject(SITE_CONFIG.organizationLogoPath),
        },
        extraData,
    );
}

export function buildOrganizationStructuredData() {
    const siteUrl = getSiteUrl();

    return buildOrganizationReference({
        '@context': 'https://schema.org',
        '@id': `${siteUrl}/#organization`,
        alternateName: SITE_CONFIG.organizationAlternateName,
        email: SUPPORT_EMAIL,
        sameAs: SITE_CONFIG.socialLinks,
    });
}

export function buildWebsiteStructuredData() {
    const siteUrl = getSiteUrl();

    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: SITE_CONFIG.organizationName,
        inLanguage: 'zh-Hant-HK',
        publisher: buildOrganizationReference(),
        potentialAction: {
            '@type': 'SearchAction',
            target: `${siteUrl}${SITE_CONFIG.searchPathTemplate}`,
            'query-input': 'required name=search_term_string',
        },
    };
}

export function buildBreadcrumbStructuredData(items = []) {
    const itemListElement = items
        .filter(item => item?.name)
        .map((item, index) => {
            const listItem = {
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
            };

            if (item.url) {
                listItem.item = toAbsoluteUrl(item.url);
            }

            return listItem;
        });

    if (itemListElement.length === 0) {
        return null;
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement,
    };
}

function buildAggregateRating(ratingAverage, ratingDistribution = []) {
    if (!ratingAverage) {
        return undefined;
    }

    const ratingCount = ratingDistribution.reduce(
        (sum, item) => sum + (Number(item?.count) || 0),
        0,
    );

    if (ratingCount <= 0) {
        return undefined;
    }

    return {
        '@type': 'AggregateRating',
        ratingValue: ratingAverage,
        ratingCount,
    };
}

export function buildArticleStructuredData({
    title,
    description,
    image,
    url,
    datePublished,
    author,
    tags = [],
}) {
    const article = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: stripHtml(description),
        mainEntityOfPage: toAbsoluteUrl(url),
        url: toAbsoluteUrl(url),
        inLanguage: 'zh-Hant-HK',
        isPartOf: {
            '@id': `${getSiteUrl()}/#website`,
        },
        publisher: buildOrganizationReference(),
    };

    const imageObject = buildImageObject(image);
    if (imageObject) {
        article.image = [imageObject.url];
    }

    if (datePublished) {
        article.datePublished = datePublished;
        article.dateModified = datePublished;
    }

    const authorReference = buildPersonReference(author);
    if (authorReference) {
        article.author = authorReference;
    }

    if (tags.length > 0) {
        article.keywords = tags;
    }

    return article;
}

export function buildPersonStructuredData({
    name,
    description,
    image,
    url,
    ratingAverage,
    ratingDistribution,
}) {
    const person = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name,
        url: toAbsoluteUrl(url),
        worksFor: buildOrganizationReference(),
    };

    const plainDescription = stripHtml(description);
    if (plainDescription) {
        person.description = plainDescription;
    }

    const imageObject = buildImageObject(image);
    if (imageObject) {
        person.image = imageObject.url;
    }

    const aggregateRating = buildAggregateRating(ratingAverage, ratingDistribution);
    if (aggregateRating) {
        person.aggregateRating = aggregateRating;
    }

    return person;
}

function buildCourseOffer({
    currency,
    priceAmountMin,
    priceAmountMax,
    offerCount,
    url,
}) {
    if (!currency || (!priceAmountMin && !priceAmountMax)) {
        return null;
    }

    if (priceAmountMin && priceAmountMax && priceAmountMin !== priceAmountMax) {
        return {
            '@type': 'AggregateOffer',
            url: toAbsoluteUrl(url),
            priceCurrency: currency,
            lowPrice: priceAmountMin,
            highPrice: priceAmountMax,
            offerCount: offerCount || undefined,
        };
    }

    return {
        '@type': 'Offer',
        url: toAbsoluteUrl(url),
        priceCurrency: currency,
        price: priceAmountMin || priceAmountMax,
    };
}

export function buildCourseStructuredData({
    title,
    description,
    image,
    url,
    teachers = [],
    languages = [],
    courseLevels = [],
    mediums = [],
    categories = [],
    priceCurrency,
    priceAmountMin,
    priceAmountMax,
    offerCount,
    ratingAverage,
    ratingDistribution,
}) {
    const course = {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: title,
        description: stripHtml(description),
        url: toAbsoluteUrl(url),
        provider: buildOrganizationReference(),
    };

    const imageObject = buildImageObject(image);
    if (imageObject) {
        course.image = imageObject.url;
    }

    const instructors = teachers
        .map(buildPersonReference)
        .filter(Boolean);
    if (instructors.length > 0) {
        course.instructor = instructors;
    }

    const displayLanguages = getDisplayNames(languages, CourseLanguage);
    if (displayLanguages.length > 0) {
        course.inLanguage = displayLanguages;
    }

    const displayLevels = getDisplayNames(courseLevels, CourseLevel);
    if (displayLevels.length > 0) {
        course.educationalLevel = displayLevels;
    }

    const displayMediums = getDisplayNames(mediums, CourseMedium);
    if (displayMediums.length > 0) {
        course.courseMode = displayMediums;
    }

    const displayCategories = getDisplayNames(categories, CourseCategory);
    if (displayCategories.length > 0) {
        course.about = displayCategories;
    }

    const offers = buildCourseOffer({
        currency: priceCurrency,
        priceAmountMin,
        priceAmountMax,
        offerCount,
        url,
    });
    if (offers) {
        course.offers = offers;
    }

    const aggregateRating = buildAggregateRating(ratingAverage, ratingDistribution);
    if (aggregateRating) {
        course.aggregateRating = aggregateRating;
    }

    return course;
}

export default function StructuredData({ data }) {
    const items = (Array.isArray(data) ? data : [data]).filter(Boolean);

    if (items.length === 0) {
        return null;
    }

    return (
        <>
            {items.map((item, index) => (
                <script
                    key={`structured-data-${index}`}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
                />
            ))}
        </>
    );
}

StructuredData.propTypes = {
    data: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object),
    ]).isRequired,
};
